import { Box, Flex, Grid, Text, Link, IconButton, Tooltip, SimpleGrid } from '@chakra-ui/react';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { IconInfoCircle } from 'assets/icons';
import Loader from 'components/Loader';
import CheckoutForm from 'containers/AccountBilling/shared/CheckoutForm';
import { initialCheckoutValue } from 'containers/AccountBilling/shared/CheckoutForm/schema';
import { transformCheckoutDataToFormdata } from 'containers/AccountBilling/shared/CheckoutForm/transformCheckoutDataToFormdata';
import { routePaths } from 'global/routePaths';
import { useFetchBillingDetails } from 'hooks/accountBilling/useFetchBillingDetails';
import { useCheckoutStore } from 'store/useCheckoutStore';

const Checkout: React.FC<unknown> = () => {
  const { t } = useTranslation(['common', 'accountBilling']);

  const [formData, setFormData] = useState<typeof initialCheckoutValue>(initialCheckoutValue);
  const { data, status } = useFetchBillingDetails();

  const { checkoutDetails } = useCheckoutStore(state => state);

  useEffect(() => {
    if (status === 'success') {
      const fetchedFormData = data?.payload?.billing_address;
      const formData = transformCheckoutDataToFormdata(fetchedFormData);

      setFormData(formData);
    }
  }, [status, data]);

  if (status === 'loading') return <Loader />;

  return (
    <Flex gridGap={8} justifyContent="center" position="relative" flexDir={['column', null, 'row']}>
      <Box
        sx={{
          '& > *': {
            py: 4,
            px: 6,
          },
          color: 'newGray.1000',
          borderRadius: 'md',
          h: 'fit-content',
          border: '1px solid',
          borderColor: 'gray.200',
          position: ['initial', null, 'sticky'],
          top: 0,
        }}
      >
        <Flex justifyContent="space-between" fontWeight="600" borderBottom="1px solid" borderColor="gray.200">
          <Text>{t('accountBilling:subscriptionPlanDetails')}</Text>
          <Link as={RouterLink} to={routePaths.accountBilling.upgrade} color="secondary.500">
            <Text>{t('accountBilling:changePlan')}</Text>
          </Link>
        </Flex>
        <Box bg="newGray.100" borderBottom="1px dashed" borderColor="gray.200">
          <Grid templateColumns={{ base: 'repeat(3, 1fr)' }} textTransform="uppercase" gridGap={6}>
            <Box>
              <Text fontSize="xs" mb={2}>
                {t('accountBilling:selectedPlan')}
              </Text>
              <Text fontWeight="600">{checkoutDetails.name}</Text>
            </Box>
            <Box>
              <Text fontSize="xs" mb={2}>
                {t('accountBilling:billingFrequency')}
              </Text>
              <Text fontWeight="600">{checkoutDetails.duration}</Text>
            </Box>
            <Box>
              <Text fontSize="xs" mb={2}>
                {t('accountBilling:costPerUser')}
              </Text>
              <Text fontWeight="600">${checkoutDetails.rate}</Text>
            </Box>
          </Grid>
        </Box>
        <Box bg="newGray.100">
          <Grid templateColumns={{ base: 'repeat(3, 1fr)' }} textTransform="uppercase">
            <Box>
              <Text fontSize="xs" mb={2}>
                {t('accountBilling:numberOfUsers')}
              </Text>
              <Text fontWeight="600">{checkoutDetails.noOfUsers}</Text>
            </Box>
            <Box>
              <Text fontSize="xs" mb={2}>
                {t('accountBilling:totalPrice')}
              </Text>
              <Text fontWeight="600">${checkoutDetails.rate * checkoutDetails.noOfUsers}</Text>
            </Box>
          </Grid>
        </Box>
        {checkoutDetails.addOns.length > 0 ? (
          <Tooltip
            label={
              <SimpleGrid columns={2}>
                {checkoutDetails.addOns.map((addOn, index: number) => {
                  return (
                    <React.Fragment key={index}>
                      <Text textAlign="left">{addOn.name}</Text>
                      <Text textAlign="right">{`$${addOn.price}${t('accountBilling:slashPerUser')}`}</Text>
                    </React.Fragment>
                  );
                })}
              </SimpleGrid>
            }
            hasArrow
            bg="primary.700"
          >
            <Flex alignItems="center" px={0} py={2}>
              <IconButton
                as={IconInfoCircle}
                aria-label="add-ons information"
                variant="unstyled"
                w="16px"
                h="16px"
                sx={{
                  path: { fill: 'primary.500' },
                }}
              />
              <Text color="primary.500">Add-Ons included</Text>
            </Flex>
          </Tooltip>
        ) : null}
      </Box>
      <Box
        sx={{
          color: 'newGray.1000',
          borderRadius: 'md',
          h: 'fit-content',
          w: 'fit-content',
          border: '1px solid',
          borderColor: 'gray.200',
          '& > *': {
            py: 4,
            px: 6,
          },
        }}
      >
        <Text fontWeight={600} mb={{ base: '2', md: '0' }} borderBottom="1px solid" borderColor="gray.200">
          {t('accountBilling:billingInfo')}
        </Text>
        <CheckoutForm duration_type={checkoutDetails?.duration} plan_name={checkoutDetails?.name} formData={formData} />
      </Box>
    </Flex>
  );
};

export default Checkout;
